/**
 * The pricing catalog, and the funnel parameters it validates.
 *
 * Amounts come from @promptgatewaysadmin/pricing or from a Stripe minor-units value — never
 * hard-coded in a component, never from an env var, and never sent to the server. The
 * catalog is a BUILD-TIME CONSTANT and is never fetched.
 */
import { catalog, getPlan, type Plan, type PlanCode, type IntervalCode } from "@promptgatewaysadmin/pricing";

export type { Plan, PlanCode, IntervalCode };
export { catalog, getPlan };

const PLAN_CODES: readonly string[] = catalog.plans.map((p) => p.code);
const INTERVAL_CODES: readonly string[] = ["month", "year"];

/** Unknown or absent plan means NO PRE-SELECTION, never a guess. */
export function isKnownPlan(value: string | null | undefined): value is PlanCode {
  return typeof value === "string" && PLAN_CODES.includes(value);
}

export function isKnownInterval(value: string | null | undefined): value is IntervalCode {
  return typeof value === "string" && INTERVAL_CODES.includes(value);
}

/** Enterprise is sales-led. It never reaches /checkout, where the contract returns 400. */
export function isSelfServe(code: PlanCode): boolean {
  return code !== "enterprise";
}

export function isPerSeat(code: PlanCode): boolean {
  return getPlan(code).perSeat;
}

/**
 * The headline amount for a checkout summary, in integer USD CENTS.
 *
 * Per-seat plans multiply by seats; Enterprise has NO amount, ever, and returns null so a
 * caller cannot accidentally render a zero. Cents keep the multiplication exact — 3060 x 7 is
 * 21420, while 30.60 x 7 is 214.20000000000002.
 *
 * On the yearly interval this is the ANNUAL TOTAL, which is what Stripe charges. It is not
 * twelve times the per-month figure the pricing page displays, and must not be derived from it.
 */
export function summaryAmount(code: PlanCode, interval: IntervalCode, seats: number): number | null {
  const plan = getPlan(code);
  if (interval === "year") {
    if (plan.annualTotalCents === null) return null;
    return plan.perSeat ? plan.annualTotalCents * seats : plan.annualTotalCents;
  }
  if (plan.monthlyCents === null) return null;
  return plan.perSeat ? plan.monthlyCents * seats : plan.monthlyCents;
}

/**
 * The seat floor for a plan. Team is 2: Checkout answers 400 SEATS_BELOW_MINIMUM below it, so
 * the stepper must never let a customer ask for a number the server will reject.
 */
export function minSeats(code: PlanCode): number {
  return getPlan(code).minSeats;
}
